// contexts/AuthContext.js - Fixed version with proper data persistence, Google verification, and first login tracking
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { 
  auth, 
  db,
  database,
  functions,
  googleProvider,
  isMobileDevice,
  getWechatAuthUrl
} from '../firebase';
import { httpsCallable } from 'firebase/functions';
import {
  ref as rtdbRef,
  onValue,
  set as rtdbSet,
  onDisconnect,
  serverTimestamp as rtdbServerTimestamp,
} from 'firebase/database';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  updateProfile,
  setPersistence,            
  browserLocalPersistence,   
  browserSessionPersistence,
  indexedDBLocalPersistence,
  sendPasswordResetEmail,
  signInWithCustomToken

} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';

const AuthContext = createContext();

// Holds the signup form data until users/{uid} is written (see signup()).
const PENDING_SIGNUP_KEY = 'pendingSignupProfile';

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(undefined);
  const [userProfile, setUserProfile] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false); // ADD THIS
  // True while signup() is between createUser and writing users/{uid}, so
  // fetchUserProfile (fired by onAuthStateChanged mid-signup) doesn't race it.
  const signupInProgressRef = useRef(false);

  // Prefer IndexedDB persistence — it survives longer than localStorage in mobile
  // in-app browsers (notably WeChat), which reduces unexpected logouts. Falls back
  // to localStorage, then session, if IndexedDB isn't available.
  async function setDurablePersistence() {
    try { await setPersistence(auth, indexedDBLocalPersistence); return; } catch (_) {}
    try { await setPersistence(auth, browserLocalPersistence); return; } catch (_) {}
    try { await setPersistence(auth, browserSessionPersistence); } catch (_) {}
  }

  // Enhanced signup function with better data persistence
  async function signup(email, password, userData) {
    try {
      // For new signups, default to durable persistence (remember the user)
      await setDurablePersistence();

      // SIGNUP FIX: remember what the user typed until their users/{uid} doc
      // is safely written. If the write below fails or the tab is closed
      // mid-signup, fetchUserProfile() uses this to rebuild the doc on the
      // next load instead of leaving them with an account but no profile
      // (which is what trapped email users on an endless "Loading profile…").
      signupInProgressRef.current = true;
      try {
        localStorage.setItem(PENDING_SIGNUP_KEY, JSON.stringify({
          email: (email || '').toLowerCase(), ...userData, at: Date.now(),
        }));
      } catch (_) {}

      // Create user account
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      
      // Mark as first login for new signup
      setIsFirstLogin(true);
      localStorage.setItem(`hasLoggedIn_${user.uid}`, 'true');
      
      // Reset first login flag after some time
      setTimeout(() => setIsFirstLogin(false), 15000);
      
      // Update display name immediately. Non-fatal: the account already
      // exists at this point, so a failure here must not surface as
      // "Failed to create account" (the user would then retry and get
      // "email already in use").
      try {
        await updateProfile(user, { displayName: userData.name });
      } catch (e) {
        console.warn('updateProfile failed (non-fatal):', e?.message);
      }

      // Create comprehensive user profile in Firestore
      const userProfileData = {
        uid: user.uid,
        name: userData.name || '',
        email: userData.email || user.email,
        phone: userData.phone || '',
        wechatId: userData.wechatId || '',
        gender: userData.gender || '',
        role: 'player', // Default role
        signupCount: 0,
        cancellationCount: 0,
        checkinCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        profileComplete: !!(userData.name && userData.gender && (userData.phone || userData.wechatId)),
        emailVerified: user.emailVerified,
        photoURL: user.photoURL || null,
        provider: 'email',
        lastLoginAt: new Date().toISOString(),
        // Additional fields for better user management
        isActive: true,
        preferences: {
          notifications: true,
          emailUpdates: true,
          smsUpdates: false
        },
        // Verification status
        verificationStatus: {
          email: user.emailVerified,
          phone: false,
          identity: false
        }
      };

      // Non-fatal with one retry — see the note at the top of signup().
      // If it still fails, the in-memory profile below lets the user carry
      // on, and fetchUserProfile() writes the doc on the next load.
      try {
        await setDoc(doc(db, 'users', user.uid), userProfileData);
        try { localStorage.removeItem(PENDING_SIGNUP_KEY); } catch (_) {}
      } catch (e) {
        console.warn('Creating user doc failed, retrying once:', e?.message);
        try {
          await new Promise(r => setTimeout(r, 1200));
          await setDoc(doc(db, 'users', user.uid), userProfileData);
          try { localStorage.removeItem(PENDING_SIGNUP_KEY); } catch (_) {}
        } catch (e2) {
          console.error('Creating user doc failed (will repair on next load):', e2?.message);
        }
      }
      signupInProgressRef.current = false;

      // Set the user profile immediately in context
      setUserProfile({
        ...userProfileData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString()
      });

      console.log('User profile created successfully:', userProfileData);
      return user;
    } catch (error) {
      signupInProgressRef.current = false;
      console.error('Signup error:', error);
      throw error;
    }
  }
async function forgotPassword(email) {
  try {
    await sendPasswordResetEmail(auth, email, {
      url: `${window.location.origin}/login`, // Redirect back to login after reset
      handleCodeInApp: false // Use the default password reset page
    });
    
    console.log('Password reset email sent successfully');
    return { success: true };
  } catch (error) {
    console.error('Forgot password error:', error);
    throw error;
  }
}

  // Enhanced login function with first login detection
  async function login(email, password) {
    try {
      // Note: setPersistence should be called BEFORE this function in Login component
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      
      // Check if this is first login for existing users
      const hasLoggedInBefore = localStorage.getItem(`hasLoggedIn_${user.uid}`);
      if (!hasLoggedInBefore) {
        setIsFirstLogin(true);
        localStorage.setItem(`hasLoggedIn_${user.uid}`, 'true');
        
        // Reset after showing modal
        setTimeout(() => setIsFirstLogin(false), 10000);
      }
      
      // Update last login time
      try {
        await updateDoc(doc(db, 'users', user.uid), {
          lastLoginAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isActive: true
        });
      } catch (updateError) {
        console.warn('Could not update last login time (offline?):', updateError);
      }

      return user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Enhanced Google login with mobile handling - Complete function for AuthContext.js
// Google sign-in creates the users/{uid} doc itself for new users. While it
// runs, tell fetchUserProfile (fired by onAuthStateChanged) not to "repair"
// a missing doc — otherwise the repair could win the race and the new user
// would miss the welcome flow / default fields. Load the profile afterwards.
async function loginWithGoogle() {
  signupInProgressRef.current = true;
  try {
    return await loginWithGoogleInner();
  } finally {
    signupInProgressRef.current = false;
    if (auth.currentUser) fetchUserProfile(auth.currentUser).catch(() => {});
  }
}

async function loginWithGoogleInner() {
  const mobile = isMobileDevice();
  console.log('Google login initiated', { mobile });

  try {
    let result;
    
    if (mobile) {
      // Mobile-specific handling with timeout and retries
      console.log('Using mobile-optimized Google login flow');
      
      try {
        result = await Promise.race([
          signInWithPopup(auth, googleProvider),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('timeout')), 25000) // 25 second timeout for mobile
          )
        ]);
      } catch (mobileError) {
        console.log('Mobile Google login failed:', mobileError);
        
        // Handle specific mobile errors
        if (mobileError.message === 'timeout') {
          throw new Error('Login timed out. Please check your internet connection and try again.');
        }
        
        if (mobileError.code === 'auth/popup-blocked') {
          throw new Error('Popup was blocked by your browser. Please enable popups and try again, or use email login instead.');
        }
        
        if (mobileError.code === 'auth/popup-closed-by-user') {
          throw new Error('Login was cancelled. Please try again.');
        }
        
        if (mobileError.code === 'auth/network-request-failed') {
          throw new Error('Network error. Please check your internet connection and try again.');
        }
        
        if (mobileError.code === 'auth/cancelled-popup-request') {
          throw new Error('Another login attempt is in progress. Please wait a moment and try again.');
        }
        
        // Re-throw other errors
        throw mobileError;
      }
    } else {
      // Desktop - standard flow
      console.log('Using desktop Google login flow');
      result = await signInWithPopup(auth, googleProvider);
    }

    const { user } = result;
    
    // Check if user profile exists
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const now = new Date();
      
      if (!userDoc.exists()) {
        // New Google user - definitely first login
        setIsFirstLogin(true);
        localStorage.setItem(`hasLoggedIn_${user.uid}`, 'true');
        
        // Reset after some time
        setTimeout(() => setIsFirstLogin(false), 15000);
        
        // New Google user - create profile but mark as incomplete
        const newUserProfile = {
          uid: user.uid,
          name: user.displayName || 'Google User',
          email: user.email,
          phone: '', // Empty - needs to be filled
          wechatId: '', // Empty - needs to be filled  
          gender: '', // Empty - needs to be filled
          role: 'player',
          signupCount: 0,
          cancellationCount: 0,
          checkinCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          profileComplete: false, // Mark as incomplete for Google users
          emailVerified: user.emailVerified,
          photoURL: user.photoURL,
          provider: 'google',
          lastLoginAt: new Date().toISOString(),
          isActive: true,
          preferences: {
            notifications: true,
            emailUpdates: true,
            smsUpdates: false
          },
          verificationStatus: {
            email: user.emailVerified,
            phone: false,
            identity: false
          },
          // Flag to indicate this is a new Google user who needs to complete profile
          needsProfileCompletion: true
        };

        await setDoc(doc(db, 'users', user.uid), newUserProfile);
        
        // Set profile in context with converted timestamps
        setUserProfile({
          ...newUserProfile,
          createdAt: now.toISOString(),
          updatedAt: now.toISOString(),
          lastLoginAt: now.toISOString()
        });

        // Show welcome message for new Google users (delay for mobile)
        setTimeout(() => {
          showGoogleWelcomeNotification();
        }, mobile ? 2000 : 1000);

      } else {
        // Existing user - check localStorage for first login tracking
        const hasLoggedInBefore = localStorage.getItem(`hasLoggedIn_${user.uid}`);
        if (!hasLoggedInBefore) {
          setIsFirstLogin(true);
          localStorage.setItem(`hasLoggedIn_${user.uid}`, 'true');
          setTimeout(() => setIsFirstLogin(false), 10000);
        }
        
        // Existing user - update login time and get profile
        const existingProfile = userDoc.data();
        
        // IMPORTANT: only sync the auth-provider photoURL back onto the
        // Firestore doc when the user has never uploaded a custom avatar.
        // Once they have (signalled by originalPhotoURL being set by
        // avatarUtils.js on first upload), their custom photoURL must be
        // preserved across sign-ins — writing user.photoURL here every
        // login is exactly what caused custom avatars to revert to the
        // Google/WeChat photo after re-authentication.
        const photoURLUpdate = existingProfile.originalPhotoURL
          ? {} // custom avatar exists — never overwrite it
          : { photoURL: user.photoURL }; // first time / no custom avatar yet

        await updateDoc(doc(db, 'users', user.uid), {
          lastLoginAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          emailVerified: user.emailVerified,
          ...photoURLUpdate,
          isActive: true
        });

        // Convert Firestore timestamps and set profile
        const profileWithDates = convertTimestampsToISO(existingProfile);
        setUserProfile(profileWithDates);
      }
      
    } catch (firestoreError) {
      console.warn('Could not access Firestore (offline?):', firestoreError);
      setIsOffline(true);
      
      // Still check for first login even if offline
      const hasLoggedInBefore = localStorage.getItem(`hasLoggedIn_${user.uid}`);
      if (!hasLoggedInBefore) {
        setIsFirstLogin(true);
        localStorage.setItem(`hasLoggedIn_${user.uid}`, 'true');
        setTimeout(() => setIsFirstLogin(false), 10000);
      }
      
      // Create a temporary profile from auth data
      setUserProfile({
        uid: user.uid,
        name: user.displayName || 'Google User',
        email: user.email,
        role: 'player',
        provider: 'google',
        photoURL: user.photoURL,
        emailVerified: user.emailVerified,
        createdAt: new Date().toISOString(),
        profileComplete: false,
        needsProfileCompletion: true
      });
    }

    console.log('Google login successful:', user.email);
    return user;
    
  } catch (error) {
    console.error('Google login error:', error);
    
    // Enhanced error handling for mobile and desktop
    if (error.message?.includes('timed out') || error.message?.includes('timeout')) {
      throw new Error('Login timed out. Please check your internet connection and try again.');
    }
    
    if (error.code === 'auth/popup-blocked') {
      if (mobile) {
        throw new Error('Popup was blocked by your browser. Please enable popups in your browser settings, or try using email/password login instead.');
      } else {
        throw new Error('Popup was blocked. Please allow popups and try again.');
      }
    }
    
    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Login was cancelled. Please try again.');
    }
    
    if (error.code === 'auth/network-request-failed') {
      throw new Error('Network error. Please check your internet connection and try again.');
    }
    
    if (error.code === 'auth/cancelled-popup-request') {
      throw new Error('Another login attempt is in progress. Please wait a moment and try again.');
    }
    
    if (error.code === 'auth/account-exists-with-different-credential') {
      throw new Error('An account already exists with this email using a different sign-in method. Please use email/password login.');
    }
    
    if (error.code === 'auth/operation-not-allowed') {
      throw new Error('Google sign-in is not enabled. Please contact support.');
    }
    
    if (error.code === 'auth/unauthorized-domain') {
      throw new Error('This domain is not authorized for Google sign-in. Please contact support.');
    }
    
    if (error.code === 'auth/web-storage-unsupported' && mobile) {
      throw new Error('Your browser settings are blocking login. Please enable cookies and local storage, then try again.');
    }
    
    if (error.code === 'auth/operation-not-supported-in-this-environment' && mobile) {
      throw new Error('Google login is not supported in this browser environment. Please try using email/password login.');
    }
    
    // Generic fallback with mobile-specific messaging
    if (mobile) {
      throw new Error('Google sign-in failed on mobile. Please try refreshing the page or use email/password login instead.');
    } else {
      throw new Error('Failed to login with Google. Please try again or use email/password login.');
    }
  }
}



  // Show welcome notification for new Google users
  const showGoogleWelcomeNotification = () => {
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 z-50 max-w-sm bg-blue-50 border border-blue-200 rounded-xl shadow-lg p-4';
    notification.innerHTML = `
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <div class="ml-3">
          <h4 class="text-sm font-semibold text-blue-800">Welcome to the Club!</h4>
          <p class="text-sm text-blue-700 mt-1">Please complete your profile to register for events. We need your contact information and gender.</p>
          <button onclick="this.parentElement.parentElement.parentElement.remove()" class="mt-2 text-xs text-blue-600 hover:text-blue-500 font-medium">
            Complete Profile Now
          </button>
        </div>
        <button onclick="this.parentElement.remove()" class="ml-4 text-blue-400 hover:text-blue-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `;
    document.body.appendChild(notification);
    
    // Auto remove after 8 seconds
    setTimeout(() => {
      if (document.body.contains(notification)) {
        notification.remove();
      }
    }, 8000);
  };

  // ── WeChat login ──────────────────────────────────────────────────
  // Step 1: redirect the WeChat in-app browser to WeChat's authorize page.
  // Control returns to /wechat-callback with a ?code (see WechatCallback.js).
  function loginWithWechat() {
    window.location.href = getWechatAuthUrl('/wechat-callback');
  }

  // Step 2: hand the WeChat code to the Cloud Function, get a Firebase custom
  // token, and sign in. onAuthStateChanged then loads the profile as usual
  // (the wechatLogin function already created the /users doc server-side).
  async function completeWechatLogin(code) {
    try {
      // keep the user signed in on this device (WeChat in-app browser).
      // IndexedDB survives WeChat's webview storage clearing better than localStorage.
      await setDurablePersistence();

      const fn = httpsCallable(functions, 'wechatLogin');
      const { data } = await fn({ code });

      const cred = await signInWithCustomToken(auth, data.token);
      const user = cred.user;

      // First-login tracking, consistent with login()/loginWithGoogle()
      const hasLoggedInBefore = localStorage.getItem(`hasLoggedIn_${user.uid}`);
      if (!hasLoggedInBefore) {
        setIsFirstLogin(true);
        localStorage.setItem(`hasLoggedIn_${user.uid}`, 'true');
        setTimeout(() => setIsFirstLogin(false), 10000);
      }

      console.log('WeChat login successful:', user.uid);
      return user;
    } catch (error) {
      console.error('WeChat login error:', error);
      throw error;
    }
  }

  // Sign out
  async function logout() {
    try {
      // Before signing out (both need the user still signed in):
      //  1. Remove this device's push token, so a shared phone doesn't keep
      //     receiving the previous account's notifications.
      //  2. Mark presence offline. This used to happen in an effect cleanup
      //     AFTER signOut, when the database rules no longer allowed the
      //     write — so logged-out users kept showing "Online now".
      try {
        const { removePushTokenOnLogout } = await import('../utils/PushNotificationService');
        await Promise.race([removePushTokenOnLogout(), new Promise(r => setTimeout(r, 3000))]);
      } catch (_) {}
      try {
        const uid = auth.currentUser?.uid;
        if (uid && database) {
          const statusRef = rtdbRef(database, `status/${uid}`);
          await onDisconnect(statusRef).cancel().catch(() => {});
          await Promise.race([
            rtdbSet(statusRef, { online: false, lastSeenAt: rtdbServerTimestamp() }),
            new Promise(r => setTimeout(r, 2000)),
          ]).catch(() => {});
        }
      } catch (_) {}

      await signOut(auth);
      setCurrentUser(null);
      setUserProfile(null);
      setIsOffline(false);
      setIsFirstLogin(false); // Reset first login flag
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  // Enhanced profile update function
  async function updateUserProfile(profileData) {
    try {
      if (!currentUser) throw new Error('No user logged in');

      // Update auth profile if name or photo changed
      const authUpdates = {};
      if (profileData.name && profileData.name !== currentUser.displayName) {
        authUpdates.displayName = profileData.name;
      }
      if (profileData.photoURL && profileData.photoURL !== currentUser.photoURL) {
        authUpdates.photoURL = profileData.photoURL;
      }

      if (Object.keys(authUpdates).length > 0) {
        await updateProfile(currentUser, authUpdates);
      }

      // Check if profile is complete after update
      const isComplete = !!(
        profileData.name && 
        profileData.gender && 
        (profileData.phone || profileData.wechatId)
      );

      // Update Firestore profile
      const updateData = {
        ...profileData,
        updatedAt: serverTimestamp(),
        profileComplete: isComplete,
        needsProfileCompletion: false // Remove flag once profile is updated
      };

      // merge-set, not updateDoc: updateDoc throws NOT_FOUND if the user doc
      // was never created (the broken-signup case this file now repairs).
      await setDoc(doc(db, 'users', currentUser.uid), updateData, { merge: true });

      // Refresh user profile immediately
      await refreshUserProfile();
      
      // Show success notification
      if (isComplete && (!userProfile?.profileComplete || userProfile?.needsProfileCompletion)) {
        showProfileCompleteNotification();
      }
    } catch (error) {
      console.error('Profile update error:', error);
      if (error.code === 'unavailable') {
        setIsOffline(true);
        throw new Error('Cannot update profile while offline. Please check your connection.');
      }
      throw error;
    }
  }

  // Show profile completion success notification
  const showProfileCompleteNotification = () => {
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 z-50 max-w-sm bg-green-50 border border-green-200 rounded-xl shadow-lg p-4';
    notification.innerHTML = `
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <svg class="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <div class="ml-3">
          <h4 class="text-sm font-semibold text-green-800">Profile Complete!</h4>
          <p class="text-sm text-green-700 mt-1">You can now register for events and participate in club activities.</p>
        </div>
        <button onclick="this.parentElement.remove()" class="ml-4 text-green-400 hover:text-green-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      if (document.body.contains(notification)) {
        notification.remove();
      }
    }, 5000);
  };

  // Helper function to convert Firestore timestamps to ISO strings
  const convertTimestampsToISO = (data) => {
    const converted = { ...data };
    
    ['createdAt', 'updatedAt', 'lastLoginAt'].forEach(field => {
      if (converted[field]) {
        if (typeof converted[field] === 'object' && converted[field].toDate) {
          converted[field] = converted[field].toDate().toISOString();
        } else if (typeof converted[field] === 'string') {
          // Already a string, keep as is
        } else {
          // Fallback to current date
          converted[field] = new Date().toISOString();
        }
      }
    });
    
    return converted;
  };

  // Fetch user profile from Firestore with better error handling.
  //
  // SIGNUP FIX: accepts the Firebase auth user (or a uid, for old callers).
  //  - Doc missing → it is CREATED (inside a transaction, so it can never
  //    overwrite a doc that signup() wrote a moment earlier), using the
  //    details saved by signup() when available. Previously this just
  //    logged "User profile not found" and left userProfile undefined, so a
  //    new email user whose doc write failed could never load My Profile.
  //  - Any read error → a placeholder profile is set (flagged _loadError),
  //    so the profile form can still open instead of spinning forever.
  //    Previously only "offline" errors did this, and even that branch read
  //    a stale `currentUser` from the auth listener's first-render closure.
  async function fetchUserProfile(userOrUid) {
    const authUser = typeof userOrUid === 'string' ? auth.currentUser : userOrUid;
    const uid = typeof userOrUid === 'string' ? userOrUid : userOrUid?.uid;
    if (!uid) return null;
    const ref = doc(db, 'users', uid);
    try {
      const userDoc = await getDoc(ref);
      if (userDoc.exists()) {
        const profileData = userDoc.data();
        const profileWithDates = convertTimestampsToISO(profileData);
        
        setUserProfile(profileWithDates);
        setIsOffline(false);
        return profileWithDates;
      }

      // signup() is writing this doc right now — let it finish.
      if (signupInProgressRef.current) return null;

      console.warn('User profile not found — creating it');
      const repaired = await createMissingUserDoc(uid, authUser);
      setUserProfile(repaired);
      setIsOffline(false);
      return repaired;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      if (error.code === 'unavailable' || String(error.message || '').includes('offline')) {
        setIsOffline(true);
      }
      // Never leave a signed-in user with no profile at all.
      const basicProfile = {
        uid,
        name: authUser?.displayName || '',
        email: authUser?.email || '',
        role: 'player',
        emailVerified: !!authUser?.emailVerified,
        photoURL: authUser?.photoURL || null,
        provider: authUser?.providerData?.[0]?.providerId === 'google.com' ? 'google' : 'email',
        createdAt: new Date().toISOString(),
        profileComplete: false,
        needsProfileCompletion: true,
        _loadError: true,
      };
      setUserProfile(prev => (prev && prev.uid === uid ? prev : basicProfile));
      return basicProfile;
    }
  }

  // Creates users/{uid} only if it still doesn't exist (transaction), filling
  // in whatever the user typed at signup if signup() didn't get to save it.
  async function createMissingUserDoc(uid, authUser) {
    let pending = null;
    try {
      const raw = localStorage.getItem(PENDING_SIGNUP_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && authUser?.email && parsed.email === authUser.email.toLowerCase()) pending = parsed;
    } catch (_) {}

    const name = pending?.name || authUser?.displayName || '';
    const gender = pending?.gender || '';
    const phone = pending?.phone || '';
    const wechatId = pending?.wechatId || '';
    const now = new Date().toISOString();
    const profileComplete = !!(name && gender && (phone || wechatId));
    const data = {
      uid,
      name,
      email: authUser?.email || pending?.email || '',
      phone,
      wechatId,
      gender,
      role: 'player',
      signupCount: 0,
      cancellationCount: 0,
      checkinCount: 0,
      createdAt: now,
      updatedAt: now,
      lastLoginAt: now,
      profileComplete,
      needsProfileCompletion: !profileComplete,
      emailVerified: !!authUser?.emailVerified,
      photoURL: authUser?.photoURL || null,
      provider: authUser?.providerData?.[0]?.providerId === 'google.com' ? 'google' : 'email',
      isActive: true,
      repairedAt: now,
    };

    const ref = doc(db, 'users', uid);
    const result = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (snap.exists()) return convertTimestampsToISO(snap.data()); // someone beat us to it
      tx.set(ref, data);
      return data;
    });
    try { localStorage.removeItem(PENDING_SIGNUP_KEY); } catch (_) {}
    return result;
  }

  // Enhanced refresh user profile function
  async function refreshUserProfile() {
    try {
      if (!currentUser) {
        console.warn('No current user to refresh profile for');
        return null;
      }

      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const profileData = userDoc.data();
        const profileWithDates = convertTimestampsToISO(profileData);
        
        setUserProfile(profileWithDates);
        setIsOffline(false);
        console.log('User profile refreshed successfully');
        return profileWithDates;
      } else {
        // SIGNUP FIX: create the missing doc instead of giving up.
        console.warn('User profile not found during refresh — creating it');
        return await fetchUserProfile(currentUser);
      }
    } catch (error) {
      console.error('Error refreshing user profile:', error);
      
      if (error.code === 'unavailable' || error.message.includes('offline')) {
        setIsOffline(true);
        console.warn('Cannot refresh profile while offline');
      }
      
      throw error;
    }
  }

  // Check if user is admin
  const isAdmin = userProfile?.role === 'admin';

  // Check if profile needs completion
  const needsProfileCompletion = userProfile?.needsProfileCompletion || 
    !userProfile?.profileComplete || 
    !(userProfile?.name && userProfile?.gender && (userProfile?.phone || userProfile?.wechatId));

 

useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    setCurrentUser(user);
    
    if (user) {
      await fetchUserProfile(user);
      
      // UPDATE LAST ACTIVITY TIME — throttled to once per 30 minutes.
      // Previously this wrote on EVERY auth state resolution (i.e. every
      // page load/reload), which triggered onSnapshot listeners elsewhere
      // (AdminDashboard, AppNotificationProvider) and caused cascading
      // Firestore reads — especially painful on mobile where it contributed
      // to reload loops and slow dashboard loads.
      try {
        const ACTIVITY_THROTTLE_MS = 30 * 60 * 1000; // 30 minutes
        const lastActivityKey = `lastActivity_${user.uid}`;
        const lastWrite = parseInt(localStorage.getItem(lastActivityKey) || '0', 10);
        const now = Date.now();
        if (now - lastWrite > ACTIVITY_THROTTLE_MS) {
          localStorage.setItem(lastActivityKey, now.toString());
          await updateDoc(doc(db, 'users', user.uid), {
            lastActivityAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            isActive: true
          });
        }
      } catch (error) {
        console.warn('Could not update last activity:', error);
      }
    } else {
      setUserProfile(null);
      setIsOffline(false);
      setIsFirstLogin(false);
    }
    
    setLoading(false);
  });

  return unsubscribe;
}, []);

// ── Live presence (Realtime Database) ───────────────────────────────────
// Tracks "is this user's tab actually open right now" for AdminDashboard's
// Users Management online/offline indicator. Deliberately separate from the
// Firestore lastActivityAt logic above, which only updates on login/reload
// and is fine for "last seen" history but can't tell you who's online now.
//
// Why Realtime Database instead of Firestore: RTDB's onDisconnect() is a
// SERVER-SIDE guarantee — when the socket drops (tab closed, network lost,
// browser killed, phone locked), Firebase's own servers fire the cleanup,
// not the client. There's no reliable equivalent in Firestore short of
// frequent "heartbeat" writes, which would cost real reads/writes and add
// actual client-side work. This approach costs the regular user exactly:
// - one tiny RTDB write when they open the app
// - one tiny RTDB write when they close it (or none at all if the
//   connection just drops — the server does that part for free)
// No polling, no intervals, no per-click writes. Regular users never read
// from this path at all (only the admin dashboard does), so this adds zero
// load to their normal use of the site.
useEffect(() => {
  if (!currentUser?.uid) return undefined;

  if (!database) {
    return undefined;
  }

  const uid = currentUser.uid;
  const userStatusRef = rtdbRef(database, `status/${uid}`);
  const connectedRef = rtdbRef(database, '.info/connected');

  // ── Idle detection ──────────────────────────────────────────────────
  // After 30 minutes of no user interaction (mouse, touch, keyboard,
  // scroll), mark the user as offline and stop the heartbeat. This
  // prevents background tabs from appearing "Online now" indefinitely
  // and saves Firestore snapshot-listener reads across the app (every
  // open tab's onSnapshot costs a read when data changes; an idle tab
  // has no value but still incurs reads).
  const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
  let idleTimer = null;
  let isIdle = false;

  const markOnline = () => {
    rtdbSet(userStatusRef, {
      online: true,
      lastSeenAt: rtdbServerTimestamp(),
    }).catch(() => {});
  };

  const markOffline = () => {
    rtdbSet(userStatusRef, {
      online: false,
      lastSeenAt: rtdbServerTimestamp(),
    }).catch(() => {});
  };

  const goIdle = () => {
    if (isIdle) return;
    isIdle = true;
    markOffline();
  };

  const resetIdleTimer = () => {
    if (idleTimer) clearTimeout(idleTimer);
    if (isIdle) {
      // Was idle → user came back — mark online again
      isIdle = false;
      markOnline();
    }
    idleTimer = setTimeout(goIdle, IDLE_TIMEOUT_MS);
  };

  // Listen for any user interaction
  const ACTIVITY_EVENTS = ['mousedown', 'touchstart', 'keydown', 'scroll'];
  // Throttle so we don't fire resetIdleTimer on every scroll pixel
  let lastActivityTs = Date.now();
  const onActivity = () => {
    const now = Date.now();
    if (now - lastActivityTs < 30000) return; // throttle to once per 30s
    lastActivityTs = now;
    resetIdleTimer();
  };

  ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, onActivity, { passive: true }));

  // Also wake up when a hidden tab becomes visible again
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      resetIdleTimer();
    }
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  // Start the idle timer
  resetIdleTimer();

  // ── RTDB presence ───────────────────────────────────────────────────
  const unsubscribe = onValue(connectedRef, (snap) => {
    if (snap.val() === false) return;

    onDisconnect(userStatusRef).set({
      online: false,
      lastSeenAt: rtdbServerTimestamp(),
    }).then(() => {
      // Only mark online if not idle
      if (!isIdle) {
        markOnline();
      }
    });
  });

  // Heartbeat: refresh lastSeenAt every 2 minutes — but only when
  // not idle and tab is visible
  const heartbeat = setInterval(() => {
    if (!isIdle && document.visibilityState === 'visible') {
      markOnline();
    }
  }, 2 * 60 * 1000);

  return () => {
    unsubscribe();
    clearInterval(heartbeat);
    if (idleTimer) clearTimeout(idleTimer);
    ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, onActivity));
    document.removeEventListener('visibilitychange', onVisibilityChange);
    markOffline();
  };
}, [currentUser?.uid]);

  // Network status listener
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      // Retry fetching profile when back online
      if (currentUser && (!userProfile || userProfile.needsProfileCompletion)) {
        fetchUserProfile(currentUser);
      }
    };

    const handleOffline = () => {
      setIsOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [currentUser, userProfile]);

  // Context value
  const value = {
    currentUser,
    userProfile,
    isAdmin,
    isOffline,
    needsProfileCompletion,
    isFirstLogin, 
    forgotPassword,
    signup,
    login,
    loginWithGoogle,
    loginWithWechat,
    completeWechatLogin,
    logout,
     updateUserProfile,
    fetchUserProfile,
    refreshUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}